import * as THREE from 'three';
import type { InputState } from '../types';
import { CollisionWorld } from '../collision/CollisionWorld';
import { clamp } from '../utils/math';

const THROTTLE_ACCEL = 9;
const BRAKE_DECEL = 26;
const REVERSE_ACCEL_SCALE = 0.6;
const MAX_FORWARD_SPEED = 9;
const MAX_REVERSE_SPEED = 4;
const COAST_FRICTION = 3.5;
const TURN_RATE = 2.4;
const STEER_SPEED_FULL = 2;
const STEER_FLOOR = 0.45;
const WALL_HIT_SPEED_RETAIN = 0.5;
const CAR_COLLISION_RADIUS = 1.4;
const CRASH_SPEED_THRESHOLD = 3.5;

export class VehicleController {
  model: THREE.Group;
  position = new THREE.Vector3();
  heading = 0;
  forwardSpeed = 0;
  onCrash?: () => void;
  private collisionWorld: CollisionWorld;
  private wasHittingWall = false;

  constructor(model: THREE.Group, collisionWorld: CollisionWorld) {
    this.model = model;
    this.collisionWorld = collisionWorld;
    this.position.copy(model.position);
    this.heading = model.rotation.y;
  }

  update(delta: number, input: InputState) {
    const accelInput = -input.moveZ; // W → -Z in input convention → forward
    const steerInput = input.moveX;

    if (accelInput > 0.1) {
      this.forwardSpeed += THROTTLE_ACCEL * accelInput * delta;
    } else if (accelInput < -0.1) {
      if (this.forwardSpeed > 0.3) {
        this.forwardSpeed -= BRAKE_DECEL * -accelInput * delta;
      } else {
        this.forwardSpeed += THROTTLE_ACCEL * accelInput * REVERSE_ACCEL_SCALE * delta;
      }
    } else {
      const decay = COAST_FRICTION * delta;
      if (Math.abs(this.forwardSpeed) < decay) this.forwardSpeed = 0;
      else this.forwardSpeed -= Math.sign(this.forwardSpeed) * decay;
    }

    this.forwardSpeed = clamp(this.forwardSpeed, -MAX_REVERSE_SPEED, MAX_FORWARD_SPEED);

    // Steering scales with speed with a floor so the car can still pivot when stopped.
    // Reverse flips so the wheel behaves naturally.
    const speedFactor = Math.max(
      STEER_FLOOR,
      Math.min(1, Math.abs(this.forwardSpeed) / STEER_SPEED_FULL),
    );
    const steerDir = this.forwardSpeed >= 0 ? 1 : -1;
    this.heading -= steerInput * TURN_RATE * speedFactor * steerDir * delta;

    this.position.x += Math.sin(this.heading) * this.forwardSpeed * delta;
    this.position.z += Math.cos(this.heading) * this.forwardSpeed * delta;

    const resolved = this.collisionWorld.resolveCollision(
      this.position.x,
      this.position.y + 0.5,
      this.position.z,
      CAR_COLLISION_RADIUS,
    );
    const hitWall = resolved.x !== this.position.x || resolved.z !== this.position.z;
    this.position.x = resolved.x;
    this.position.z = resolved.z;
    if (hitWall) {
      if (!this.wasHittingWall && Math.abs(this.forwardSpeed) > CRASH_SPEED_THRESHOLD) {
        this.onCrash?.();
      }
      this.forwardSpeed *= WALL_HIT_SPEED_RETAIN;
    }
    this.wasHittingWall = hitWall;

    this.model.position.copy(this.position);
    this.model.rotation.y = this.heading;
  }
}
